import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import classNames from 'classnames';

import {
    Colours,
    Sizes,
    Paragraph,
    Typography,
    IconPaper,
    IconPhone,
    IconNote,
    PillCaption,
    PageBehaviourButton,
    getFormattedNumber,
    isAddressComplete,
    FormattedStreetAddress,
    navigateWindowTo,
} from '@ratehub/base-ui';
import { getTransactionTypeFromPurpose } from '@ratehub/documents/mtg-journey-document/stable';
import { getScenarioTypeFromPurpose } from '@ratehub/mtg-common';


dayjs.extend(advancedFormat);

/* eslint-disable react/no-multi-comp */
function MortgageApplicationRow({ document, isMostRecent }) {
    const intl = useIntl();

    const { address, type, amount, modified, id, resumeHREF, bookAgentHREF, uploadDocsHREF } = document ?? {};

    const isToday = document ? dayjs(modified).isSame(dayjs(), 'day') : null;
    const dateFormat = document ? dayjs(modified).format('MMMM Do, YYYY') : null;
    const timeFormat = document ? dayjs(modified).format('h:mma') : null;

    const mortgageTypeLabelId = `${id}-mortgage-type-label`;
    const mortgageAmountLabelId = `${id}-mortgage-amount-label`;
    const lastModifiedLabelId = `${id}-last-modified-label`;

    return (
        <Container
            data-name="dashboard-mortgages-row"
        >
            <Choose>
                <When condition={!!document}>
                    <If condition={isMostRecent}>
                        <PillCaption
                            className="pill"
                            backgroundColour={Colours.YUZU}
                            message={intl.formatMessage(Messages.MOST_RECENT)}
                        />
                    </If>

                    <div className="applicationRow">
                        <div className="applicationDetails applicationColumn">
                            <h4 className={
                                classNames(
                                    'address',
                                    Typography.CLASSES.TITLE_XS,
                                    Typography.CLASSES.WEIGHT_MEDIUM,
                                )}
                            >
                                <Choose>
                                    <When condition={isAddressComplete(address)}>
                                        <FormattedStreetAddress
                                            address={address}
                                            shouldIncludeCity={true}
                                            shouldIncludePostalCode={true}
                                        />
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="accounts.MortgagesApplicationRow.mortgageRowTitle"
                                            defaultMessage="{
                                            type, select,
                                                preapproval {Pre-approval}
                                                refinance {Refinance}
                                                renewal {Renewal}
                                                heloc {HELOC}
                                                other {Purchase}
                                        }"
                                            values={{ type: getScenarioTypeFromPurpose(type).replace('-', '') }}
                                        />
                                    </Otherwise>
                                </Choose>
                            </h4>
                            <div className="detailsLine">
                                <p className={
                                    classNames(
                                        Typography.CLASSES.TEXT_XS,
                                        Typography.CLASSES.WEIGHT_MEDIUM,
                                        Typography.CLASSES.LEADING_L,
                                    )}
                                >
                                    <FormattedMessage
                                        id="accounts.MortgageApplicationRow.mortgageTypeLabel"
                                        defaultMessage="<label>Mortgage type: </label><value>{mortgageType}</value>"
                                        values={{
                                            label: chunks => (
                                                <span
                                                    className="detailsLabel"
                                                    id={mortgageTypeLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            value: chunks => (
                                                <span
                                                    className={classNames('detailsValue', 'type', 'rh-fg-blackberry')}
                                                    aria-labelledby={mortgageTypeLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            mortgageType: (
                                                <FormattedMessage
                                                    id="accounts.MortgageApplicationRow.typeString"
                                                    defaultMessage="{
                                                        type, select,
                                                            preapproval {Pre-approval}
                                                            refinance {Refinance}
                                                            renewal {Renewal}
                                                            heloc {HELOC}
                                                            other {Purchase}
                                                    }"
                                                    values={{ type: getTransactionTypeFromPurpose(type).replace('-', '') }}
                                                />
                                            ),
                                        }}
                                    />
                                </p>
                            </div>
                            <div className="detailsLine">
                                <p className={
                                    classNames(
                                        Typography.CLASSES.TEXT_XS,
                                        Typography.CLASSES.WEIGHT_MEDIUM,
                                        Typography.CLASSES.LEADING_L,
                                    )}
                                >
                                    <FormattedMessage
                                        id="accounts.MortgageApplicationRow.mortgageAmountLabel"
                                        defaultMessage="<label>Mortgage amount: </label><value>{mortgageAmount}</value>"
                                        values={{
                                            label: chunks => (
                                                <span
                                                    className="detailsLabel"
                                                    id={mortgageAmountLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            value: chunks => (
                                                <span
                                                    className={classNames('detailsValue', 'rh-fg-blackberry')}
                                                    aria-labelledby={mortgageAmountLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            mortgageAmount: getFormattedNumber(amount, 'CAD0', intl),
                                        }}
                                    />
                                </p>
                            </div>
                            <div className="detailsLine">
                                <p className={
                                    classNames(
                                        Typography.CLASSES.TEXT_XS,
                                        Typography.CLASSES.WEIGHT_MEDIUM,
                                        Typography.CLASSES.LEADING_L,
                                    )}
                                >
                                    <FormattedMessage
                                        id="accounts.MortgageApplicationRow.lastModifiedLabel"
                                        defaultMessage="<label>Last modified: </label>
                                            <value>{isToday, select,
                                                true {Today, {timeFormat}}
                                                other {{dateFormat}}
                                            }</value>
                                        "
                                        values={{
                                            label: chunks => (
                                                <span
                                                    className="detailsLabel"
                                                    id={lastModifiedLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            value: chunks => (
                                                <span
                                                    className={classNames('detailsValue', 'rh-fg-blackberry')}
                                                    aria-labelledby={lastModifiedLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            timeFormat: timeFormat,
                                            dateFormat: dateFormat,
                                            isToday: isToday,
                                        }}
                                    />
                                </p>
                            </div>
                        </div>
                        <div className="applicationActions applicationColumn">
                            <PageBehaviourButton
                                onClick={() => navigateWindowTo(resumeHREF)}
                                variant="dark"
                                size="small"
                                iconLocation="left"
                                iconComponent={IconPaper}
                                includeIconOutline={false}
                                message={Messages.RESUME_APPLICATION}
                                data-name="dashboard-mortgages-resumeApplication"
                            />

                            <If condition={bookAgentHREF}>
                                <PageBehaviourButton
                                    onClick={() => navigateWindowTo(bookAgentHREF)}
                                    variant="dark"
                                    size="small"
                                    iconLocation="left"
                                    iconComponent={IconPhone}
                                    includeIconOutline={false}
                                    message={Messages.BOOK_CALL_WITH_AGENT}
                                    data-name="dashboard-mortgages-bookCall"
                                />
                            </If>

                            <If condition={uploadDocsHREF}>
                                <PageBehaviourButton
                                    onClick={() => navigateWindowTo(uploadDocsHREF)}
                                    variant="dark"
                                    size="small"
                                    iconLocation="left"
                                    iconComponent={IconNote}
                                    includeIconOutline={false}
                                    message={Messages.UPLOAD_MORTAGE_DOCUMENTS}
                                    data-name="dashboard-mortgages-uploadDocuments"
                                />
                            </If>
                        </div>
                    </div>
                </When>
                <Otherwise>
                    <Paragraph
                        className="no-document"
                        size="medium"
                        message={Messages.NO_QUOTES}
                    />
                </Otherwise>
            </Choose>
        </Container>
    );
}
MortgageApplicationRow.propTypes = {
    document: PropTypes.shape({
        id: PropTypes.string,
        address: PropTypes.object,
        type: PropTypes.string,
        amount: PropTypes.number,
        modified: PropTypes.string,
        resumeHREF: PropTypes.string,
        bookAgentHREF: PropTypes.string,
        uploadDocsHREF: PropTypes.string,
    }),
    isMostRecent: PropTypes.bool,
};

MortgageApplicationRow.defaultProps = {
    document: undefined,
    isMostRecent: false,
};

const Container = styled.section`
    display: flex;
    flex-direction: column;
    padding: ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.ONE};
    > .pill {
        width: fit-content;
        font-weight: ${Typography.WEIGHTS.MEDIUM};
        font-size: ${Sizes.FONTS['2XS']};
        margin: 0 0 ${Sizes.SPACING.THREE_QUARTERS};
    }
    > .applicationRow {
        justify-content: space-between;
        flex-direction: row;
        flex-wrap: wrap;
        display: flex;
        > .applicationColumn {
            &:first-child {
                margin-right: ${Sizes.SPACING.ONE_AND_A_HALF};
            }
            &.applicationDetails {
                min-width: 200px;
                margin: ${Sizes.SPACING.THREE_QUARTERS} 0 ${Sizes.SPACING.ONE};
                > .address {
                    font-size: ${Sizes.FONTS.M};
                    font-weight: ${Typography.WEIGHTS.MEDIUM};
                    text-transform: capitalize;
                    margin: 0 0 ${Sizes.SPACING.ONE_AND_A_HALF};
                }
                > .detailsLine {
                    p {
                        margin: 0;
                        font-size: ${Sizes.FONTS['2XS']};
                        font-weight: ${Typography.WEIGHTS.MEDIUM};
                        line-height: ${Sizes.LINE_HEIGHT.XL}
                    }
                    .detailsLabel {
                        text-transform: uppercase;
                        color: ${Colours.STONE_DARKEST};
                    }
                    .detailsValue {
                        font-weight: ${Typography.WEIGHTS.MEDIUM};
                    }
                    .detailsValue.type {
                        text-transform: uppercase;
                    }
                }
            }
            &.applicationActions {
                min-width: 250px;
                > button {
                    min-height: 48px;

                    > .applicationIcon {
                        width: 2.5rem;
                        margin-left: -10px;
                    }
                    &:hover {
                        > .applicationIcon {
                            stroke: ${Colours.BLUEBERRY};
                        }
                    }
                }
            }
        }
    }
    .no-document {
        text-align: center;
        margin: ${Sizes.SPACING.ONE_AND_A_HALF};
        color: ${Colours.STONE_DARKEST};
    }
    .document {
        margin: ${Sizes.SPACING.TWO};
        height: ${Sizes.SPACING.SEVEN};
        color: ${Colours.BLUEBERRY};
    }
`;

const Messages = defineMessages({
    MOST_RECENT: {
        id: 'accounts.MortgageApplicationRow.mostRecentPill',
        defaultMessage: 'Your most recent application',
    },
    RESUME_APPLICATION: {
        id: 'AccountApp.MortgageApplicationRow.resumeApplication',
        defaultMessage: 'resume application',
    },
    BOOK_CALL_WITH_AGENT: {
        id: 'AccountApp.MortgageApplicationRow.callAgent',
        defaultMessage: 'book a call with an agent',
    },
    UPLOAD_MORTAGE_DOCUMENTS: {
        id: 'AccountApp.MortgageApplicationRow.uploadDocuments',
        defaultMessage: 'upload documents',
    },
    NO_QUOTES: {
        id: 'AccountApp.MortgageApplicationRow.noQuotes',
        defaultMessage: 'You have no mortgage applications in progress.',
    },
});

export default observer(MortgageApplicationRow);
